<template>
  <div class="flex flex-col w-full h-full gap-2 pb-4 overflow-y-auto">
    <div class="flex flex-col w-full h-full items-center">
      <qrcode-vue
        v-if="selectedVoucher"
        :value="selectedVoucher._id"
        :size="150"
        level="H"
        renderAs="svg"/>
      <div class="flex flex-row w-full flex-grow items-center mt-8 ">
        <div class="flex flex-col w-1/2 h-full items-center">
          <span class="flex gap-2 flex-row w-full items-center font-semibold"><i class="pi-calendar pi"/> <span>נרכש בתאריך</span> </span>
          <span class="flex gap-2 flex-row w-full items-center">{{
              DateTime.fromISO(selectedVoucher.validFromDate as string).toLocaleString(DateTime.DATE_SHORT, {locale: 'he-IL'})
            }}</span>
          <span class="flex gap-2 mt-4 flex-row w-full items-center font-semibold"><i
            class="pi-calendar pi"/> <span>בתוקף עד</span> </span>
          <span class="flex gap-2 flex-row w-full items-center">{{
              DateTime.fromISO(selectedVoucher.validUntilDate as string).toLocaleString(DateTime.DATE_SHORT, {locale: 'he-IL'})
            }}</span>
          <span class="flex gap-2 mt-4 flex-row w-full items-center font-semibold"><i class="pi-send pi"/> <span>נשלח אל</span> </span>
          <span class="flex gap-2 flex-row w-full items-center">{{
              selectedVoucher.sentTo === 'self' ? 'בית העסק' : selectedVoucher.sentTo
            }}</span>
        </div>
        <div class="flex flex-col w-1/2 h-full items-center">
          <span class="flex gap-2 flex-row w-full items-center font-semibold"><i class="pi-money-bill pi"/> <span>סוג שובר</span> </span>
          <span class="flex gap-2 flex-row w-full items-center">{{
              selectedVoucher.voucherType === VoucherTypes.SERVICE ? 'שירות בהנחה' : 'ערך כספי'
            }}</span>
          <span class="flex gap-2 mt-4 flex-row w-full items-center font-semibold"><i
            class="pi-percentage pi"/> <span>שווי השובר</span> </span>
          <span class="flex gap-2 flex-row w-full items-center">{{
              selectedVoucher.voucherType === VoucherTypes.SERVICE ? `${selectedVoucher.serviceDiscount}%` : `${selectedVoucher.amount} ש"ח`
            }}</span>
        </div>
      </div>
      <div v-if="selectedVoucher.personalMessage" class="w-full mt-6 mb-2 p-3 rounded-lg bg-primary-50 flex flex-col gap-1">
        <span class="flex gap-2 flex-row items-center font-semibold"><i class="pi-comment pi" /> <span>הודעה אישית</span></span>
        <span class="text-sm whitespace-pre-line">{{ selectedVoucher.personalMessage }}</span>
      </div>

      <div class="flex w-full gap-2">
        <Button
          v-if="showPaymentButton"
          label=" תשלום "
          outlined
          class=" w-full h-12 mb-2"
          icon="pi pi-credit-card"
          @click="openPaymentDrawer"
        />
        <Button
          v-if="!selectedVoucher.used && selectedVoucher.sentTo !== 'self'"
          label=" שליחה חוזרת "
          outlined
          icon="pi pi-send"
          class=" w-full h-12 mb-2"
          @click="sendVoucher(selectedVoucher._id)"
        />
      </div>
      <Button
        v-if="!selectedVoucher.used"
        label=" מימוש שובר "
        class="w-full h-12 mt-auto"
        @click="markVoucherAsUsed(selectedVoucher._id)"
      />
    </div>
  </div>

</template>

<script setup lang="ts">

import QrcodeVue from 'qrcode.vue';
import { computed, PropType, ref } from 'vue';
import { IVoucher, VoucherTypes } from '@torim-app/types';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import { useAdminStore } from '@app/stores';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';
import { useDrawer } from '@torim-app/shared-components';
const { openDrawer, closeDrawer } = useDrawer();

const toast = useToast();
const adminStore = useAdminStore();

const props = defineProps({
  selectedVoucher: {
    type: Object as PropType<IVoucher & { isPaid: boolean }>,
    required: true,
    default: () => {},
  }
})

const emit = defineEmits(['fetch-vouchers'])

const showPaymentButton = computed(() => {
  return (adminStore.businessSiteData?.hasPayments || adminStore.businessSiteData?.hasInvoices) &&
    !props.selectedVoucher.used && props.selectedVoucher.sentTo !== 'self' && !props.selectedVoucher.isPaid;
})

const closePaymentOptions = () => {
  closeDrawer();
  emit('fetch-vouchers');
};

const openPaymentDrawer = () => {
  openDrawer(PaymentOptions, {
    uuid: props.selectedVoucher._id,
    payFor: 'voucher',
    voucherServices: props.selectedVoucher.serviceId,
    onClose: closePaymentOptions
  }, { title: 'אפשרויות תשלום' });
};

const markVoucherAsUsed = (voucherId: string) => {
  PromotionsApi.markVoucherAsUsed(voucherId)
    .then(_ => {
      toast.success('השובר מומש 😀');
      emit('fetch-vouchers');
      closeDrawer();
    })
    .catch((err) => {
      console.log('markVoucherAsUsed failed:', err);
      if (err === 'WRONG')
        toast.error('שובר לא קיים');
      else if (err === 'USED')
        toast.error('שובר זה כבר מומש בעבר');
      else if (err === 'EXPIRED')
        toast.error('שובר פג תוקף');
      else
        toast.error('לא הצלחנו לממש את השובר');
    })
}

const sendVoucher = (voucherId: string) => {
  PromotionsApi.sendVoucher(voucherId)
    .then(_ => {
      toast.success('ההודעה נשלחה 😀');
    })
    .catch((err) => {
      console.log('sendVoucher failed:', err);
      toast.error('לא הצלחנו לשלוח את השובר');
    })
}

</script>

<style scoped lang="scss">

</style>
